import React from 'react';
import './ErrorBanner.scss';
import PropTypes from "prop-types";
import { FiAlertCircle, FiAlertTriangle } from "react-icons/fi";

const parseWarningCountdown = (message, isWarning) => {
    if (!isWarning) {
        return { mainMessage: message, countdownText: null };
    }

    const rawText = String(message ?? "");
    const trimmedEnd = rawText.trimEnd();

    if (!trimmedEnd.endsWith(")")) {
        return { mainMessage: message, countdownText: null };
    }

    const openParenIndex = trimmedEnd.lastIndexOf("(");
    if (openParenIndex <= 0) {
        return { mainMessage: message, countdownText: null };
    }

    const mainMessage = trimmedEnd.slice(0, openParenIndex).trimEnd();
    const bracketContent = trimmedEnd.slice(openParenIndex + 1, -1).trim();
    const prefix = "disappears in ";

    if (!bracketContent.toLowerCase().startsWith(prefix)) {
        return { mainMessage: message, countdownText: null };
    }

    const secondsToken = bracketContent.slice(prefix.length).trim();
    if (!secondsToken.toLowerCase().endsWith("s")) {
        return { mainMessage: message, countdownText: null };
    }

    const secondsValue = secondsToken.slice(0, -1).trim();
    const isAllDigits = secondsValue.length > 0 && [...secondsValue].every((char) => char >= "0" && char <= "9");

    if (!isAllDigits) {
        return { mainMessage: message, countdownText: null };
    }

    return {
        mainMessage: mainMessage || message,
        countdownText: `${secondsValue}s`,
    };
};

const ErrorBanner = ({ message, variant = "error" }) => {
    const isWarning = variant === "warning";
    const Icon = isWarning ? FiAlertTriangle : FiAlertCircle;
    const { mainMessage, countdownText } = parseWarningCountdown(message, isWarning);

    return (
        <div
            className={`error-banner ${isWarning ? "error-banner--warning" : "error-banner--error"}`}
            role="status"
            aria-live="polite"
        >
            <span className={`error-icon-wrap ${isWarning ? "error-icon-wrap--warning" : "error-icon-wrap--error"}`}>
                <Icon className={`error-icon ${isWarning ? "error-icon--warning" : "error-icon--error"}`} />
            </span>
            <div className="error-content">
                <span className={`error-text ${isWarning ? "error-text--warning" : "error-text--error"}`}>{mainMessage}</span>
                {countdownText && (
                    <span className="error-countdown">{countdownText}</span>
                )}
            </div>
        </div>
    );
};

ErrorBanner.propTypes = {
    message: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(["error", "warning"]),
};

export default ErrorBanner;
